import { utc } from '@date-fns/utc';
import {
  Button,
  Divider,
  Form,
  HStack,
} from '@metservicedev/met-ui';
import { format, parse } from 'date-fns';
import React from 'react';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  ApiVolcano,
  ApiVolcanoZod,
  ApiVona,
  ApiVonaZod,
  UNKNOWN_VOLCANO_NAME,
  UNKNOWN_VOLCANO_NUMBER,
} from '@vona/shared';
import {
  isValueDurationType,
  SimpleDurationType,
  ValueDurationType,
} from '@vona/shared/Duration';
import { GetCurrentVonaResponseZod } from '@vona/shared/GetCurrentVona';

import { CloudInputs } from '../components/CloudInputs';
import { FollowupInputs } from '../components/FollowupInputs';
import { FormSection } from '../components/FormSection';
import { NoticeInputs } from '../components/NoticeInputs';
import { SubmissionDialog, SubmitState } from '../components/SubmissionDialog';
import { VolcanoInputs } from '../components/VolcanoInputs';
import { useApi } from '../context/ApiProvider';
import { useVolcanoes } from '../hooks/useVolcanoes';
import { FormVona, RawFormVona, validateVona } from '../types/FormVona';
import { formatNotamCoordinates } from '../util/coordinates';

interface Props {
  className?: string;
}

export const CreateVona: React.FC<Props> = ({ className }) => {
  const form = useForm<FormVona>({
    defaultValues: {
      status: 'OPERATIONAL',
      cloud: {
        state: 'YES_HEIGHT_KNOWN',
        height: {
          extendsAbove: false,
        },
      },
      willUpdate: true,
    },
    mode: 'onBlur',
    resolver: validateVona,
  });

  const api = useApi();
  const { volcanoes } = useVolcanoes();
  const { volcanoNumber: urlVolcanoNumber } = useParams();
  const [searchParams] = useSearchParams();
  const [submitState, setSubmitState] = React.useState<SubmitState>();

  // Set relevant fields when volcano is selected (from dropdown or by URL parameter)
  const onSelectVolcano = React.useCallback(async (volcanoNumber: string, initFromPrevious?: boolean) => {
    let volcano: ApiVolcano | undefined;
    try {
      if (volcanoNumber !== UNKNOWN_VOLCANO_NUMBER) {
        const res = await api.get(`/volcano/${volcanoNumber}`);
        volcano = ApiVolcanoZod.parse(res.data.volcano);
      }
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(`Failed to fetch volcano for ${volcanoNumber}`, e);
    }

    let vona: ApiVona | undefined;
    try {
      const res = await api.get(`/vona/${volcanoNumber}`);
      const parsed = GetCurrentVonaResponseZod.parse(res.data);
      vona = parsed.vona;

      const formatUtcStr = (isoDate: string): string => {
        return format(new Date(isoDate), 'dd/MM/yyyy HH:mm', { in: utc });
      };

      // Determine the latest notice number for selected volcano
      const noticeNumber = vona.noticeNumber.replace(/(\d+)\/(\d+)$/, (_, year, number) => {
        if (parseInt(year, 10) === new Date().getFullYear()) {
          return `${year}/${String(parseInt(number, 10) + 1)}`;
        }
        return `${new Date().getFullYear()}/1`;
      });
      form.setValue('noticeNumber', noticeNumber);

      // Set previous color code to current color code
      form.setValue('previousColorCode', vona.colorCode);

      if (initFromPrevious) {
        const { activity, duration } = vona;
        const lastEmission = 'lastVaEmission' in activity ? activity.lastVaEmission : undefined;
        let cloudState: RawFormVona['cloud']['state'] = 'NO';
        if (vona.cloud) {
          cloudState = vona.cloud.height ? 'YES_HEIGHT_KNOWN' : 'YES_HEIGHT_UNKNOWN';
        }
        form.setValue('status', vona.status);
        form.setValue('colorCode', vona.colorCode);
        form.setValue('svo', vona.svo);
        form.setValue('activity.status', activity.status);
        form.setValue('activity.lastVaEmission', lastEmission ? formatUtcStr(lastEmission) : undefined);
        if (vona.onsetTime === 'NIL' || vona.onsetTime === 'UNKNOWN') {
          form.setValue('onset.type', vona.onsetTime);
        } else {
          form.setValue('onset.type', 'KNOWN');
          form.setValue('onset.time', formatUtcStr(vona.onsetTime));
        }
        if (isValueDurationType(duration.type)) {
          form.setValue('duration.type', 'VALUE');
          form.setValue('duration.unit', duration.type as ValueDurationType);
        } else {
          form.setValue('duration.type', duration.type as SimpleDurationType);
        }
        form.setValue('duration.value', 'value' in duration ? String(duration.value) : undefined);
        form.setValue('cloud.state', cloudState);
        const cloudHeight = vona.cloud?.height?.value;
        form.setValue('cloud.height.value', cloudHeight === undefined ? undefined : String(cloudHeight));
        form.setValue('cloud.height.unit', vona.cloud?.height?.unit);
        form.setValue('cloud.height.from', vona.cloud?.height?.from);
        form.setValue('cloud.height.extendsAbove', vona.cloud?.height?.extendsAbove);
        form.setValue('cloud.height.source', vona.cloud?.height?.source);
        form.setValue('cloud.movement', vona.cloud?.movement);
        form.setValue('contacts', vona.contacts);
        form.setValue('remarks', vona.remarks);
        form.setValue('nextNotice', vona.nextNotice ? formatUtcStr(vona.nextNotice) : undefined);
        form.setValue('willUpdate', !!vona.nextNotice);
      }
    } catch (e: any) {
      if (e.response?.status === 404) {
        form.setValue('noticeNumber', `${new Date().getFullYear()}/1`);
      }
    }

    // Fill in volcano details
    form.setValue('volcano.number', volcanoNumber);
    if (volcano || vona) {
      let coordinates = '';
      if (volcano?.latitude !== undefined && volcano?.longitude !== undefined) {
        coordinates = formatNotamCoordinates(volcano.latitude, volcano.longitude);
      }
      form.setValue('volcano.name', volcano?.name || vona?.volcano.name);
      form.setValue('volcano.coordinates', [coordinates || vona?.volcano.coordinates].flat());
      form.setValue('volcano.area', volcano?.area || vona?.volcano.area);
      const vonaHeight = vona?.volcano.summitHeightAmsl;
      const height = volcano?.summitElevationMetres ?? (vonaHeight === 'UNKNOWN' ? 'UNKNOWN' : vonaHeight?.value);
      form.setValue('volcano.summitHeightAmsl.value', height === undefined ? undefined : String(height));
      const vonaHeightUnit = vonaHeight === 'UNKNOWN' ? undefined : vonaHeight?.unit;
      const heightUnit = volcano?.summitElevationMetres !== undefined ? 'M' : vonaHeightUnit;
      form.setValue('volcano.summitHeightAmsl.unit', heightUnit);
    }

    form.clearErrors();
  }, [form.setValue, volcanoes]);

  // Set volcano from URL parameter
  React.useEffect(() => {
    if (urlVolcanoNumber) {
      onSelectVolcano(urlVolcanoNumber, searchParams.get('new') === null);
    } else {
      form.reset();
    }
  }, [urlVolcanoNumber]);

  return (
    <FormProvider {...form}>
      <Form
        className={className}
        gap={4}
        role="form"
        onSubmit={form.handleSubmit(
          async ({ cloud, duration, volcano, ...data }) => {
            const volcanoHeight = volcano?.summitHeightAmsl;
            const formDateToIso = (dateStr: string) => {
              const parsedDate = parse(dateStr, 'dd/MM/yyyy HH:mm', new Date(), { in: utc });
              return parsedDate.toISOString();
            };

            const preparedVona: DeepPartial<ApiVona> = {
              ...data,
              issuedAt: new Date().toISOString(),

              volcano: {
                ...volcano,
                summitHeightAmsl: volcanoHeight?.value?.match(/^UNKNOWN$/i) ? 'UNKNOWN' : {
                  ...volcanoHeight,
                  value: Number(volcanoHeight?.value ?? 0),
                },
              },

              onsetTime: data.onset?.type === 'KNOWN' ? formDateToIso(data.onset.time ?? '') : data.onset?.type,

              duration: duration?.type === 'VALUE' ? {
                type: duration.unit,
                value: Number(duration.value),
              } : { type: duration?.type },

              cloud: cloud?.state === 'NO' ? null : {
                ...cloud,
                height: cloud?.state === 'YES_HEIGHT_KNOWN' ? {
                  ...cloud.height,
                  value: Number(cloud.height?.value),
                } : null,
              },

              nextNotice: data.willUpdate ? formDateToIso(data.nextNotice ?? '') : null,
            };
            const parsed = ApiVonaZod.safeParse(preparedVona);
            // The following case is unlikely, and difficult to test, but still
            // worth checking for and handling. So we'll just ignore coverage.
            // istanbul ignore if -- @preserve
            if (!parsed.success) {
              // eslint-disable-next-line no-console
              console.error('Failed to parse vona', parsed.error);
              setSubmitState({
                error: 'Unable to submit VONA because some fields are invalid.',
                details: 'Please check all fields are valid.',
              });
              return;
            }
            try {
              const res = await api.post('/vona', parsed.data);
              if (res.status === 207) {
                setSubmitState({ error: res.data.error, details: 'Please contact support.' });
              } else if (res.status === 200) {
                setSubmitState({ success: true });
              } else {
                setSubmitState({
                  error: `Unknown error occurred (received ${res.status} code).`,
                  details: 'Please contact support.',
                });
              }
            } catch (e: any) {
              // eslint-disable-next-line no-console
              console.error('Failed to submit vona', e);
              setSubmitState({ error: 'Failed to submit VONA', details: 'Please contact support.' });
            }
          },
          (errors) => {
            // eslint-disable-next-line no-console
            console.error('Unable to submit vona', errors);
            setSubmitState({
              error: 'Unable to submit VONA because some fields are invalid.',
              details: 'Please check all fields are valid.',
            });
          },
        )}
      >
        <FormSection label="Volcano">
          <VolcanoInputs
            onSelectUnknown={() => {
              form.setValue('volcano.number', UNKNOWN_VOLCANO_NUMBER);
              form.setValue('volcano.name', UNKNOWN_VOLCANO_NAME);
              form.setValue('volcano.coordinates', ['']);
              form.setValue('volcano.summitHeightAmsl.value', undefined);
              form.setValue('volcano.summitHeightAmsl.unit', undefined);
              form.setValue('volcano.area', '');
              form.setValue('noticeNumber', undefined);
              form.setValue('colorCode', undefined);
              form.setValue('previousColorCode', undefined);
              form.clearErrors();
            }}
            onSelectVolcano={onSelectVolcano}
            urlVolcanoNumber={urlVolcanoNumber}
            volcanoes={volcanoes}
          />
        </FormSection>
        <Divider />
        <FormSection label="Notification">
          <NoticeInputs />
        </FormSection>
        <Divider />
        <FormSection label="Volcanic Ash Cloud">
          <CloudInputs />
        </FormSection>
        <Divider />
        <FormSection label="Details">
          <FollowupInputs />
        </FormSection>
        <HStack justifyContent="center">
          <Button
            id="form-submit-button"
            data-testid="form-submit-button"
            type="submit"
            marginBottom={10}
            width="40"
          >
            Submit
          </Button>
        </HStack>
        <SubmissionDialog
          isSubmitting={form.formState.isSubmitting}
          onClose={() => setSubmitState(undefined)}
          submitState={submitState}
        />
      </Form>
    </FormProvider>
  );
};
